import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output, inject } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { encrypt } from '@por/consumer-login/shared-lib';
import { Language } from '@por/shared/core';
import { User } from '../models';
import { ConsumerLoginConfig } from '../models/consumer-login-config';
import { LocalStorageService } from '../services/local-storage.service';
import { PrivacyPolicyService } from '../services/privacy-policy.service';
import { AppFormValidators } from '../validators/app.validators';

@Component({
    standalone: false,
    selector: 'por-signup',
    templateUrl: './signup-component.component.html',
    styleUrls: ['./signup-component.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SignupComponentComponent implements OnInit {
    private readonly fb = inject(FormBuilder);
    private readonly translateService = inject(TranslateService);
    private readonly privacyPolicyService = inject(PrivacyPolicyService);
    private readonly localStorage = inject(LocalStorageService);

    @Output()
    readonly selectedTemplate: EventEmitter<string> = new EventEmitter<string>();

    @Output()
    readonly signupData: EventEmitter<User> = new EventEmitter<User>();

    @Output()
    readonly loading$: EventEmitter<boolean> = new EventEmitter<boolean>();

    inputs: ConsumerLoginConfig | undefined;
    @Input() config: string | undefined;
    @Input() isDisableButton = false;

    signupForm!: FormGroup;

    async ngOnInit(): Promise<void> {
        this.translateService.use(Language.EN_GB);
        this.initializeForm();

        if (this.config && this.config.trim() !== '') {
            this.inputs = JSON.parse(this.config) as ConsumerLoginConfig;
        }

        this.setInitialFormValues();
    }

    initializeForm() {
        const formConfig: { [key: string]: unknown } = {
            username: ['', [Validators.required, AppFormValidators.emailValidator, Validators.maxLength(128)]],
            firstName: ['', [Validators.required, Validators.maxLength(128)]],
            lastName: ['', [Validators.required, Validators.maxLength(128)]],
            companyName: ['', [Validators.maxLength(128)]],
            phoneNumber: ['', [Validators.required, Validators.maxLength(15)]],
            password: ['', [Validators.required, Validators.minLength(6), Validators.maxLength(50)]]
        };

        if (this.privacyPolicyService.isPrivacyTermsDisplayed()) {
            formConfig['privacyPolicyAccepted'] = [false, Validators.requiredTrue];
        }

        this.signupForm = this.fb.group(formConfig);
    }

    private setInitialFormValues(): void {
        const initialValues: { [key: string]: unknown } = {
            username: '',
            firstName: '',
            lastName: '',
            companyName: '',
            phoneNumber: '',
            password: ''
        };

        if (this.privacyPolicyService.isPrivacyTermsDisplayed()) {
            initialValues['privacyPolicyAccepted'] = false;
        }

        this.signupForm.setValue(initialValues);
    }

    get username(): FormControl {
        return this.signupForm.get('username') as FormControl;
    }

    get firstName(): FormControl {
        return this.signupForm.get('firstName') as FormControl;
    }

    get lastName(): FormControl {
        return this.signupForm.get('lastName') as FormControl;
    }

    get companyName(): FormControl {
        return this.signupForm.get('companyName') as FormControl;
    }

    get phoneNumber(): FormControl {
        return this.signupForm.get('phoneNumber') as FormControl;
    }

    get password(): FormControl {
        return this.signupForm.get('password') as FormControl;
    }

    get privacyPolicyAccepted(): FormControl {
        return this.signupForm.get('privacyPolicyAccepted') as FormControl;
    }

    signup(): boolean {
        const email: string = this.signupForm.value.username as string;
        const password: string = this.signupForm.value.password as string;
        let companyName: string | undefined | null;

        if (!this.signupForm.valid) {
            return false;
        }

        // Set privacy policy as accepted if user accepted it during signup
        this.localStorage.privacyPolicyAccepted = true;

        if (this.signupForm.value.companyName && this.signupForm.value.companyName.trim() !== '' && !['na', 'none', 'n/a'].includes((this.signupForm.value.companyName as string).toLowerCase())) {
            companyName = this.signupForm.value.companyName;
        }

        /* eslint-disable @typescript-eslint/naming-convention */
        /**
         * Note camelCase: DB model
         */
        this.signupData.emit({
            Email: email,
            Password: encrypt(password),
            Name: `${this.signupForm.value.firstName as string} ${this.signupForm.value.lastName as string}`,
            FirstName: this.signupForm.value.firstName as string,
            LastName: this.signupForm.value.lastName as string,
            CompanyName: companyName,
            PhoneNumber: this.signupForm.value.phoneNumber as string,
            StoreName: this.inputs?.storeName ?? ''
        } as User);
        this.loading$.emit(true);
        return true;
    }

    setTemplate(templateName: string): void {
        this.selectedTemplate.emit(templateName);
    }
}
